import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getDashboardKpis, listRecentSignups, getPilotHealth, formatCAD,
  type AdminDashboardKpis, type AdminRecentSignup, type AdminPilotHealthRow,
  type AdminPilotHealthLevel,
} from '@/lib/adminBilling';
import { humanize } from '@/lib/errors';
import { formatDate } from '@/lib/dates';

/**
 * /admin — cockpit overview.
 *
 * KPI cards across the top, recent signups + per-org pilot health below.
 * Pulls from admin_dashboard_kpis, admin_recent_signups, admin_pilot_health
 * (all SECURITY DEFINER, gated on assert_platform_admin).
 */
export default function AdminOverview() {
  const [kpis, setKpis] = useState<AdminDashboardKpis | null>(null);
  const [signups, setSignups] = useState<AdminRecentSignup[]>([]);
  const [health, setHealth] = useState<AdminPilotHealthRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getDashboardKpis(), listRecentSignups(10), getPilotHealth()])
      .then(([k, s, h]) => {
        if (cancelled) return;
        setKpis(k); setSignups(s); setHealth(h); setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(humanize(err)); setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <p className="text-sm text-muted-foreground">Loading cockpit…</p>;
  if (error)   return <p className="text-sm text-red-600">{error}</p>;
  if (!kpis)   return null;

  // Active orgs chip — sum trial+pilot+active and show "no plan" bucket
  // for orphans (organizations row exists but no org_subscriptions row).
  const accountedFor =
    kpis.trial_count + kpis.pilot_count + kpis.active_count +
    kpis.past_due_count + kpis.canceled_count;
  const noPlanCount = Math.max(0, kpis.total_orgs - accountedFor);
  const chipParts = [
    `${kpis.trial_count} trial`,
    `${kpis.pilot_count} pilot`,
    `${kpis.active_count} active`,
  ];
  if (kpis.past_due_count > 0) chipParts.push(`${kpis.past_due_count} past due`);
  if (kpis.canceled_count > 0) chipParts.push(`${kpis.canceled_count} canceled`);
  if (noPlanCount > 0)         chipParts.push(`${noPlanCount} no plan`);

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h2 className="text-2xl font-semibold tracking-tight">Cockpit overview</h2>
        <p className="text-sm text-muted-foreground">
          Cross-org metrics. Click into any card to drill in.
        </p>
      </header>

      <section aria-label="KPIs" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi
          label="Active orgs"
          value={String(kpis.total_orgs)}
          chip={chipParts.join(' · ')}
          to="/admin/orgs"
        />
        <Kpi
          label="MRR (estimated)"
          value={formatCAD(Number(kpis.mrr_estimated_cad))}
          chip={`${kpis.active_count} paying org${kpis.active_count === 1 ? '' : 's'}`}
          to="/admin/subscriptions?status=active"
          highlight
        />
        <Kpi
          label="Trials ending in 7 days"
          value={String(kpis.trials_ending_7d)}
          chip="Convert or extend before they expire"
          to="/admin/subscriptions?status=trial"
          warn={kpis.trials_ending_7d > 0}
        />
        <Kpi
          label="Past due"
          value={String(kpis.past_due_count)}
          chip="Needs follow-up"
          to="/admin/subscriptions?status=past_due"
          danger={kpis.past_due_count > 0}
        />
      </section>

      <section aria-label="Quick actions" className="rounded-lg border border-border bg-card p-4 flex gap-2 flex-wrap">
        <span className="text-xs uppercase tracking-wider text-muted-foreground self-center mr-2">
          Quick actions
        </span>
        <Link to="/admin/settings" className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted/40">
          Maintenance mode
        </Link>
        <Link to="/admin/banners/new" className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted/40">
          Post banner
        </Link>
        <Link to="/admin/subscriptions" className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted/40">
          Manage subscriptions
        </Link>
        <Link to="/admin/audit" className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted/40">
          View audit log
        </Link>
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-lg border border-border bg-card p-4 space-y-3">
          <header className="flex items-baseline justify-between">
            <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
              Recent signups
            </h3>
            <span className="text-xs text-muted-foreground">
              {signups.length === 0
                ? 'None yet'
                : `Last ${signups.length} · ${kpis.signups_7d} this week`}
            </span>
          </header>
          {signups.length === 0 ? (
            <p className="text-sm text-muted-foreground">No signups yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {signups.map((s) => (
                <li key={s.organization_id} className="flex items-center justify-between gap-2 py-2 text-sm">
                  <Link to={`/admin/orgs/${s.organization_id}`} className="hover:underline truncate">
                    {s.display_name}
                  </Link>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatDate(s.org_created_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <PilotHealthPanel rows={health} />
      </div>
    </div>
  );
}

function PilotHealthPanel({ rows }: { rows: AdminPilotHealthRow[] }) {
  const counts = rows.reduce(
    (acc, r) => { acc[r.health] = (acc[r.health] ?? 0) + 1; return acc; },
    {} as Record<AdminPilotHealthLevel, number>,
  );
  const summary = [
    counts.red     ? `${counts.red} red`         : null,
    counts.amber   ? `${counts.amber} amber`     : null,
    counts.green   ? `${counts.green} green`     : null,
    counts.no_plan ? `${counts.no_plan} no-plan` : null,
  ].filter(Boolean).join(' · ');

  return (
    <section className="rounded-lg border border-border bg-card p-4 space-y-3">
      <header className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
          Pilot pharmacy health
        </h3>
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {summary || 'No orgs yet'}
        </span>
      </header>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No orgs yet.</p>
      ) : (
        <ul className="divide-y divide-border">
          {rows.map((r) => (
            <li key={r.organization_id} className="py-2 text-sm">
              <div className="flex items-center gap-2">
                <HealthDot level={r.health} />
                <Link to={`/admin/orgs/${r.organization_id}`} className="hover:underline truncate font-medium">
                  {r.display_name}
                </Link>
                {r.tier && (
                  <span className="text-[11px] uppercase tracking-wider text-muted-foreground rounded-sm border border-border px-1 py-0.5">
                    {r.tier}
                  </span>
                )}
                {r.status && (
                  <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    {r.status}
                  </span>
                )}
              </div>
              <div className="ml-5 mt-0.5 text-xs text-muted-foreground">
                {r.reason} · {r.active_location_count} loc · {r.active_staff_count} staff
                {r.last_owner_signin_at && (
                  <> · owner seen {formatDate(r.last_owner_signin_at)}</>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function HealthDot({ level }: { level: AdminPilotHealthLevel }) {
  const color =
    level === 'green'   ? 'bg-emerald-500'   :
    level === 'amber'   ? 'bg-amber-500'     :
    level === 'red'     ? 'bg-red-600'       :
    /* no_plan */         'bg-muted-foreground/40';
  const title =
    level === 'green'   ? 'Healthy'                 :
    level === 'amber'   ? 'Needs attention'         :
    level === 'red'     ? 'Action required'         :
    /* no_plan */         'No subscription record';
  return (
    <span
      aria-label={title}
      title={title}
      className={`inline-block w-2.5 h-2.5 rounded-full ${color}`}
    />
  );
}

function Kpi({
  label, value, chip, to, highlight = false, warn = false, danger = false,
}: {
  label: string; value: string; chip?: string; to: string;
  highlight?: boolean; warn?: boolean; danger?: boolean;
}) {
  const valueColor = danger
    ? 'text-red-700'
    : warn
      ? 'text-amber-700'
      : highlight
        ? undefined
        : undefined;
  const valueStyle = highlight && !danger && !warn
    ? { color: 'hsl(var(--brand-navy))' }
    : undefined;

  return (
    <Link
      to={to}
      className="rounded-lg border border-border bg-card p-4 hover:bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 block"
    >
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-3xl font-semibold mt-1 ${valueColor ?? ''}`} style={valueStyle}>
        {value}
      </div>
      {chip && <div className="text-xs text-muted-foreground mt-1">{chip}</div>}
    </Link>
  );
}
